import React, {useContext, useState} from 'react';
import { Button } from '@mui/material';
import SimpleVerifyOTPForm from './SimpleVerifyOTPForm';
import PasswordConfirmationForm from './PasswordConfirmationForm';
import InputPassword from "../../../common/components/Inputs/InputPassword";
import WithActions from "../../../common/components/Wrappers/WithActions";
import ButtonPrimary from "../../../common/components/Buttons/ButtonPrimary";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import {ThemeContext} from "styled-components";
import DisabledFormState from "../../../common/components/Wrappers/DisabledFormState";

const ForgotPasswordConfirmMiniForm = ({
    verificationCode,
    onChangeAuthForm,
    password,
    confirmPassword,
    validateNewPasswordMatch,
    onSubmit,
    onLoginError,
    disabled = false,
    isLoading = false,
    currentStep,
}) => {
    const [passwordMismatchError, setPasswordMismatchError] = useState(false);

    const theme = useContext(ThemeContext);
    const handlePasswordChange = (e) => {
        // Clear error state when user starts typing
        if (passwordMismatchError) {
            setPasswordMismatchError(false);
        }
        onChangeAuthForm(e);
    };

    if (disabled) {
        return <DisabledFormState message="Reset" tooltip="Password Reset" />
    }

    return (
        <Box
            component="form"
            onSubmit= {(e) => {
                e.preventDefault();
                if (validateNewPasswordMatch && !validateNewPasswordMatch()) {
                    // Handle password mismatch error
                    setPasswordMismatchError(true);
                    if (onLoginError) {
                        onLoginError('New passwords do not match');
                    }
                    return;
                }
                setPasswordMismatchError(false);
                if (onSubmit) {
                    onSubmit();
                }
            }}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                minHeight: 0,
                opacity: disabled ? 0.6 : 1,
                pointerEvents: disabled ? 'none' : 'auto',
                borderRadius: '8px',
                transition: 'all 0.3s ease'
            }}
        >
            <Box sx={{ mt: -1 }}>
                <WithActions
                    forceSpacing
                    inputType="text"
                >
                    <Typography
                        component="div"
                        sx={{
                            color: theme.colors.text,
                            textAlign: 'left',
                            width: '100%',
                            fontSize: '1.75rem',
                            fontWeight: 400,
                            margin: 0,
                            padding: 0,
                            lineHeight: '40px',
                            height: '40px',
                            display: 'block',
                        }}
                    >
                        Verify Email
                    </Typography>
                </WithActions>
            </Box>
            <br/>
            {/* Form fields - Scrollable area */}
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    overflowY: 'auto',
                    mb: 2
                }}
            >
                { !disabled && (
                    <>
                    <WithActions
                        isCompleted={false}
                        successTooltip="verification code verified"
                        inputType="code"
                        isRefreshing={false}
                        showRefreshButton={true}
                        refreshTooltip="Resend verification email"
                        forceSpacing={true}
                    >
                        <SimpleVerifyOTPForm
                            verificationCode={verificationCode || ''}
                            onChangeAuthForm={onChangeAuthForm || (() => {})}
                            disabled={disabled || currentStep === 'setupMFA' || currentStep === 'verifyMFA'}
                            buttonState="resend"
                            checkmarkTooltip="verification code verified"
                        />
                    </WithActions>
                    <Box sx={{ mb: 2 }} />
                    </>
                )}

                {/* Password fields - show when OTP has 6 digits OR when MFA steps are active */}
                {((verificationCode && verificationCode.length === 6) || currentStep === 'setupMFA' || currentStep === 'verifyMFA') && (
                    <>
                        <WithActions
                            disabled={disabled || isLoading}
                            showSuccessButton={disabled || currentStep === 'setupMFA' || currentStep === 'verifyMFA'}
                            isVisible={true}
                            successTooltip="confirmed"
                            inputType="text"
                            forceSpacing={true}
                            fullWidth
                        >
                            <InputPassword
                    name="newPassword"
                    value={password || ''}
                    handleChange={handlePasswordChange}
                    disabled={disabled}
                    error={passwordMismatchError}
                    tooltipEnabled={passwordMismatchError}
                    requirementsText={[
                        "",
                        <div>
                            Password must include:
                            <br />* 12 - 36 Characters
                            <br />* Uppercase & Lowercase letters
                            <br />* Numbers
                            <br />* One special character: ^$*.[]{}()?-"!@#%&/\,':;|_~`+=
                        </div>,
                        ""
                            ]}
                            autoComplete="new-password"
                        />
                        </WithActions>
                        { !disabled && (
                        <WithActions
                            disabled={disabled || isLoading || currentStep === 'setupMFA' || currentStep === 'verifyMFA'}
                            showSuccessButton={disabled || currentStep === 'setupMFA' || currentStep === 'verifyMFA'}
                            isVisible={true}
                            inputType="text"
                            forceSpacing={true}
                            fullWidth
                        >
                            <InputPassword
                                name="confirmNewPassword"
                                value={confirmPassword || ''}
                                handleChange={handlePasswordChange}
                                disabled={disabled}
                                error={passwordMismatchError}
                                tooltipEnabled={passwordMismatchError}
                                requirementsText={[
                                    "",
                                    <div>
                                        Passwords must match
                                    </div>,
                                    ""
                                ]}
                                autoComplete="password"
                                label="Confirm"
                            />
                        </WithActions>
                        )}
                    </>
                )}
            </Box>

            {/* Submit Button - Pinned at bottom */}
            { !disabled && (
            <Box
                sx={{
                    flexShrink: 0,
                    pt: 2
                }}
            >
                <WithActions forceSpacing={true} isButton={true}>
                    <ButtonPrimary
                        type="submit"
                        disabled={disabled || isLoading || currentStep === 'setupMFA' || currentStep === 'verifyMFA'}
                    >
                        Confirm Reset
                    </ButtonPrimary>
                </WithActions>
            </Box>
            )}
        </Box>
    );
};

export default ForgotPasswordConfirmMiniForm;
